/**
 * Formats price input with:
 * - Auto-comma insertion for thousands
 * - Treats comma as decimal separator (replaces , with .)
 * - Enforces decimal precision
 * - Prevents invalid characters
 */
export function formatPriceInput(value: string, decimals: number): string {
  if (!value) return '';

  let integerPart = '';
  let decimalPart = '';
  let isDecimal = false;

  // Check for Dot (.) - Unambiguous decimal separator
  if (value.includes('.')) {
    const parts = value.split('.');
    // Integer part is everything before the first dot
    integerPart = parts[0];
    // Decimal part is everything after the first dot (join remaining in case of multiple dots)
    decimalPart = parts.slice(1).join('');
    isDecimal = true;
  }
  // Check for Trailing Comma (,) - User typing decimal separator
  // We only treat comma as decimal if it's the LAST character, to avoid conflict with thousands separators
  else if (value.endsWith(',')) {
    integerPart = value.slice(0, -1);
    decimalPart = '';
    isDecimal = true;
  }
  // No Dot and No Trailing Comma -> Treat strictly as Integer
  else {
    integerPart = value;
    isDecimal = false;
  }

  // Clean the parts
  // Remove all non-numerics from integer part (strips existing commas)
  integerPart = integerPart.replace(/[^0-9]/g, '');
  // Remove all non-numerics from decimal part
  decimalPart = decimalPart.replace(/[^0-9]/g, '');

  // Handle leading zeros in integer part
  if (integerPart.length > 1 && integerPart.startsWith('0')) {
    integerPart = integerPart.replace(/^0+/, '');
    if (integerPart === '') integerPart = '0';
  }

  // Enforce max value (999,999,999)
  if (integerPart !== '') {
    const numValue = parseInt(integerPart, 10);
    if (!isNaN(numValue) && numValue > 999999999) {
      integerPart = '999999999';
    }
  }

  // Enforce decimal precision
  if (isDecimal) {
    decimalPart = decimalPart.substring(0, decimals);
  }

  // Format integer part with commas
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Construct result
  if (isDecimal) {
    // If user typed dot/comma but no decimals yet, ensure we show the dot
    return `${formattedInteger}.${decimalPart}`;
  } else {
    return formattedInteger;
  }
}
